import { parseDay, type DashboardPeriod, type DayRange } from "./periodPresets";

const MONTHS_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

interface DayParts {
  day: number;
  month: number;
  year: number;
}

function parts(value: string): DayParts | null {
  const date = parseDay(value);
  if (!date) return null;
  return {
    day: date.getUTCDate(),
    month: date.getUTCMonth(),
    year: date.getUTCFullYear(),
  };
}

const dayMonth = (value: DayParts) =>
  `${value.day} ${MONTHS_GENITIVE[value.month]}`;

/**
 * Диапазон дней по-русски (спека 2026-10-16 §3.1): «1-16 октября 2026»,
 * «28 сентября - 4 октября 2026», «20 декабря 2025 - 5 января 2026». Без
 * года, если withYear false и обе даты в одном году.
 */
export function formatDayRange(range: DayRange, withYear = true): string {
  const from = parts(range.from);
  const to = parts(range.to);
  if (!from || !to) return "";
  if (from.year !== to.year) {
    return `${dayMonth(from)} ${from.year} - ${dayMonth(to)} ${to.year}`;
  }
  const year = withYear ? ` ${to.year}` : "";
  if (from.month !== to.month) {
    return `${dayMonth(from)} - ${dayMonth(to)}${year}`;
  }
  if (from.day === to.day) return `${dayMonth(to)}${year}`;
  return `${from.day}-${to.day} ${MONTHS_GENITIVE[to.month]}${year}`;
}

/**
 * Строка под заголовком дашборда: выбранный период и с чем сравниваем.
 * Год у сравнения пишется, только когда он отличается от года периода.
 * «Всё время» показывает фактическое начало из ответа бэкенда.
 */
export function formatPeriodCaption(
  period: DashboardPeriod,
  actualFrom?: string | null
): string {
  if (period.preset === "allTime") {
    const from = actualFrom ? actualFrom.slice(0, 10) : period.range.from;
    return `${formatDayRange({ from, to: period.range.to })}, без сравнения`;
  }
  const main = formatDayRange(period.range);
  if (!period.compare) return main;
  const sameYear =
    parts(period.compare.to)?.year === parts(period.range.to)?.year;
  return `${main}, сравнение с ${formatDayRange(period.compare, !sameYear)}`;
}
